import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import type { LocalizedText } from '../../../lib/localizedText'
import Button from '../../../components/ui/Button'
import Badge from '../../../components/ui/Badge'
import type { OperationCode } from '../types'

interface ColumnOpts {
  t: TFunction
  /** Ad kolonu tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  onEdit: (row: OperationCode) => void
  /**
   * Salt okunur kip (126 — Kısıtlı Yetkili asistan): işlemler kolonu hiç çizilmez. Ad, modül ve durum
   * tabloda zaten göründüğü için ayrı bir görüntüleme eylemine gerek yoktur.
   */
  readOnly?: boolean
}

/**
 * İşlem Kodu tablosu sütunları (045).
 *
 * <p>Bilinçli olarak **yok olanlar**: hiçbir sütun `sortable` değildir (düzen sunucuda sabit —
 * FR-002b); durum sütununda `InputSwitch` **yoktur** (salt-okuma — FR-002a); satır aksiyonlarında
 * **yalnız Düzenle** vardır, "Sil" yoktur (FR-009).
 *
 * <p>Ad **tek kolondur** ve değeri seçili dilden gelir (24.09.2026 kullanıcı kararı; anayasa → Page
 * Standards). Bu ekran uzun süre bilinçli bir **istisnaydı**: TR ve EN adı ayrı kolonlardaydı ve
 * İngilizce adı boş olan hücre boş bırakılırdı (eski FR-002c), çünkü ekranın kurulma amacı eksik
 * çevirileri göz taramasıyla bulup tamamlamaktı. İstisna, tüm yönetim tablolarını tek kurala bağlayan
 * karar lehine kaldırıldı; bedeli açıkça kabul edildi — eksik İngilizce adlar artık **listeden**
 * ayırt edilemez, yalnız düzenleme formunda görünür (spec 045 → Clarifications 2026-09-24).
 */
export function operationCodeColumns({ t, localized, onEdit, readOnly = false }: ColumnOpts): ReactElement[] {
  const columns = [
    <Column
      key="name"
      header={t('admin.operationCodes.columns.name')}
      body={(row: OperationCode) => localized(row.nameTr, row.nameEn)}
    />,
    <Column
      key="moduleName"
      field="moduleName"
      header={t('admin.operationCodes.columns.module')}
      body={(row: OperationCode) => row.moduleName ?? ''}
    />,
    <Column
      key="status"
      header={t('admin.operationCodes.columns.status')}
      style={{ width: '8rem' }}
      body={(row: OperationCode) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active
              ? t('admin.operationCodes.status.active')
              : t('admin.operationCodes.status.passive')}
          </Badge>
        )
      }}
    />,
    <Column
      key="actions"
      header={t('admin.operationCodes.columns.actions')}
      style={{ width: '6rem' }}
      body={(row: OperationCode) => (
        <div className="admin-row-actions">
          <Button
            icon="pi pi-pencil"
            rounded
            text
            label={t('admin.operationCodes.actions.edit')}
            aria-label={t('admin.operationCodes.actions.edit')}
            onClick={() => onEdit(row)}
          />
        </div>
      )}
    />,
  ]
  // Salt okunurda tek eylem (Düzenle) düşer → kolon boş kalacağı için tümüyle çıkarılır.
  return readOnly ? columns.filter((c) => c.key !== 'actions') : columns
}
